import { useState, useMemo } from "react";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Card, CardContent } from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/project/karuna/components/ui/dialog"; // @ts-ignore - dialog is not a valid module
import { Download, Search, Eye, Users, Loader2 } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { DynamicModuleService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import {
  KarunaPagination,
  ListCardViewToggle,
  type KarunaViewMode,
} from "@/project/karuna/components/shared/KarunaListControls";
import { useKarunaPagedList } from "@/project/karuna/hooks/useKarunaPagedList";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";

const SUBMISSION_SCHEMA_NAME = "Activity Submission";
const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";
const ACTIVITY_SCHEMA_NAME = "Activity Management";

// Normalize mobile number helper
const normalizeMobileNumber = (mobileNumber: string): string => {
  return String(mobileNumber ?? "")
    .replace(/\D/g, "")
    .slice(0, 10);
};

interface CertificateSubmission {
  id: string;
  school_name: string;
  kc_no: string;
  activity_title: string;
  score: number;
  status: string;
}

const AdminCertificates = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewSubmission, setPreviewSubmission] =
    useState<CertificateSubmission | null>(null);
  const [viewMode, setViewMode] = useState<KarunaViewMode>("list");

  // Fetch all submissions
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery({
    queryKey: ["admin-certificates-submissions", SUBMISSION_SCHEMA_NAME],
    queryFn: async () => {
      try {
        const allSubmissions =
          await DynamicModuleService.getGridColItemsAllPages(
            SUBMISSION_SCHEMA_NAME
          );

        const submissionsWithFullData = await mapWithConcurrency(
          allSubmissions,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (submission: any) => {
            const submissionId = submission.id ?? submission._id;
            if (!submissionId) {
              return submission;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                SUBMISSION_SCHEMA_NAME,
                submissionId
              );
              const data = fullData?.data ?? fullData ?? submission;
              return { ...submission, ...data };
            } catch (err) {
              console.warn(
                `Could not fetch full data for submission ${submissionId}:`,
                err
              );
              return submission;
            }
          }
        );

        return submissionsWithFullData;
      } catch (error) {
        console.error("Error fetching submissions:", error);
        return [];
      }
    },
  });

  // Fetch school profiles
  const { data: schoolProfilesData } = useQuery({
    queryKey: ["school-profiles-certificates", SCHOOL_PROFILE_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridColItemsAllPages(
          SCHOOL_PROFILE_SCHEMA_NAME
        );
      } catch (error) {
        console.error("Error fetching school profiles:", error);
        return [];
      }
    },
  });

  // Fetch activities
  const { data: activitiesData } = useQuery({
    queryKey: ["activities-certificates", ACTIVITY_SCHEMA_NAME],
    queryFn: async () => {
      try {
        const activitiesList =
          await DynamicModuleService.getGridColItemsAllPages(
            ACTIVITY_SCHEMA_NAME
          );

        const activitiesWithFullData = await mapWithConcurrency(
          activitiesList,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (activity: any) => {
            const activityId = activity.id ?? activity._id;
            if (!activityId) {
              return activity;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                ACTIVITY_SCHEMA_NAME,
                activityId
              );
              const data = fullData?.data ?? fullData ?? activity;
              return { ...activity, ...data };
            } catch (err) {
              console.warn(
                `Could not fetch full data for activity ${activityId}:`,
                err
              );
              return activity;
            }
          }
        );

        return activitiesWithFullData;
      } catch (error) {
        console.error("Error fetching activities:", error);
        return [];
      }
    },
  });

  // Create activity map
  const activityMap = useMemo(() => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
      return new Map<string, string>();
    }
    const map = new Map<string, string>();
    activitiesData.forEach((activity: any) => {
      const activityId = String(activity.id ?? activity._id ?? "").trim();
      const title =
        activity.title ??
        activity.activitytitle ??
        activity.activity_title ??
        "Unknown Activity";
      if (activityId) {
        map.set(activityId, title);
      }
    });
    return map;
  }, [activitiesData]);

  // Process eligible submissions for certificates
  const eligibleSubmissions = useMemo((): CertificateSubmission[] => {
    if (!submissionsData || !Array.isArray(submissionsData)) {
      return [];
    }

    const processed = submissionsData
      .map((submission: any): CertificateSubmission | null => {
        const submissionId = submission.id ?? submission._id ?? "";
        if (!submissionId) {
          return null;
        }

        // Only include approved submissions with scores
        const rawStatus = submission.status ?? submission.Status ?? "pending";
        const normalizedStatus = String(rawStatus).toLowerCase().trim();
        if (normalizedStatus !== "approved") {
          return null;
        }

        const score = submission.score ?? null;
        if (score === null || score === undefined) {
          return null;
        }

        // Get school contact for matching
        const submissionContact = normalizeMobileNumber(
          submission.schoolcontact ??
            submission.contact ??
            submission.school_contact ??
            submission.basicInfo?.schoolcontact ??
            ""
        );

        // Find matching school profile
        const schoolProfile = schoolProfilesData?.find((profile: any) => {
          const profileContact = normalizeMobileNumber(
            profile.contact ??
              profile.school_contact ??
              profile.schoolcontact ??
              ""
          );
          return profileContact === submissionContact && profileContact !== "";
        });

        // Get activity title
        const eventId = String(
          submission.event_id ?? submission.eventid ?? ""
        ).trim();
        let activityTitle = "Unknown Activity";
        if (eventId) {
          const mappedTitle = activityMap.get(eventId);
          if (mappedTitle) {
            activityTitle = mappedTitle;
          }
        }
        if (activityTitle === "Unknown Activity") {
          activityTitle =
            submission.activitytitle ??
            submission.activity_title ??
            submission.title ??
            "Unknown Activity";
        }

        return {
          id: submissionId,
          school_name:
            schoolProfile?.schoolname ??
            submission.schoolname ??
            "Unknown School",
          kc_no: schoolProfile?.kcno ?? submission.kcno ?? "N/A",
          activity_title: activityTitle,
          score: Number(score),
          status: normalizedStatus,
        };
      })
      .filter((s): s is CertificateSubmission => s !== null)
      .sort((a, b) => b.score - a.score); // Sort by score descending

    return processed;
  }, [submissionsData, schoolProfilesData, activityMap]);

  // Filter submissions by search term
  const filteredSubmissions = useMemo(() => {
    if (!searchTerm.trim()) {
      return eligibleSubmissions;
    }

    const query = searchTerm.toLowerCase();
    return eligibleSubmissions.filter(
      sub =>
        sub.school_name.toLowerCase().includes(query) ||
        sub.kc_no.toLowerCase().includes(query) ||
        sub.activity_title.toLowerCase().includes(query)
    );
  }, [eligibleSubmissions, searchTerm]);

  const {
    page: certPage,
    setPage: setCertPage,
    totalPages: certTotalPages,
    paginatedItems: pagedCertSubmissions,
    totalItems: certFilteredTotal,
    pageSize: certPageSize,
    setPageSize: setCertPageSize,
  } = useKarunaPagedList(filteredSubmissions, searchTerm);

  // Calculate stats
  const stats = useMemo(() => {
    const total = eligibleSubmissions.length;
    const excellence = eligibleSubmissions.filter(s => s.score >= 80).length;
    const merit = eligibleSubmissions.filter(
      s => s.score >= 60 && s.score < 80
    ).length;
    const participation = eligibleSubmissions.filter(s => s.score < 60).length;

    return { total, excellence, merit, participation };
  }, [eligibleSubmissions]);

  // Get certificate type badge
  const getCertificateBadge = (score: number) => {
    if (score >= 80) {
      return (
        <Badge className="border-0 bg-yellow-500 text-white">Excellence</Badge>
      );
    } else if (score >= 60) {
      return <Badge className="border-0 bg-gray-500 text-white">Merit</Badge>;
    } else {
      return (
        <Badge className="border-0 bg-red-500 text-white">Participation</Badge>
      );
    }
  };

  // Generate PDF certificate
  const generateCertificatePDF = async (submission: CertificateSubmission) => {
    try {
      const doc = await generateCertificatePDFDoc(submission);

      // Generate filename
      const filename = `Certificate_${submission.school_name.replace(
        /[^a-z0-9]/gi,
        "_"
      )}_${submission.kc_no}_${Date.now()}.pdf`;

      // Save PDF
      doc.save(filename);

      toast({
        title: "Success",
        description: "Certificate downloaded successfully",
      });
    } catch (error) {
      console.error("Error generating certificate:", error);
      toast({
        title: "Error",
        description: "Failed to generate certificate",
        variant: "destructive",
      });
    }
  };

  // Helper function to generate certificate PDF (used by both preview and download)
  const generateCertificatePDFDoc = async (
    submission: CertificateSubmission
  ) => {
    const jsPDFModule = await import("jspdf");
    const jsPDF = jsPDFModule.default;

    const doc = new jsPDF("l", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const centerX = pageWidth / 2;
    const margin = 15;

    // Set cream background
    doc.setFillColor(255, 253, 245);
    doc.rect(0, 0, pageWidth, pageHeight, "F");

    // Yellow decorative border with corners
    doc.setDrawColor(255, 193, 7); // Yellow
    doc.setLineWidth(3);
    doc.rect(margin, margin, pageWidth - margin * 2, pageHeight - margin * 2);

    // Decorative corner elements
    const cornerSize = 15;
    const cornerOffset = margin + 3;
    // Top-left corner
    doc.setLineWidth(2);
    doc.line(
      cornerOffset,
      cornerOffset,
      cornerOffset + cornerSize,
      cornerOffset
    );
    doc.line(
      cornerOffset,
      cornerOffset,
      cornerOffset,
      cornerOffset + cornerSize
    );
    // Top-right corner
    doc.line(
      pageWidth - cornerOffset,
      cornerOffset,
      pageWidth - cornerOffset - cornerSize,
      cornerOffset
    );
    doc.line(
      pageWidth - cornerOffset,
      cornerOffset,
      pageWidth - cornerOffset,
      cornerOffset + cornerSize
    );
    // Bottom-left corner
    doc.line(
      cornerOffset,
      pageHeight - cornerOffset,
      cornerOffset + cornerSize,
      pageHeight - cornerOffset
    );
    doc.line(
      cornerOffset,
      pageHeight - cornerOffset,
      cornerOffset,
      pageHeight - cornerOffset - cornerSize
    );
    // Bottom-right corner
    doc.line(
      pageWidth - cornerOffset,
      pageHeight - cornerOffset,
      pageWidth - cornerOffset - cornerSize,
      pageHeight - cornerOffset
    );
    doc.line(
      pageWidth - cornerOffset,
      pageHeight - cornerOffset,
      pageWidth - cornerOffset,
      pageHeight - cornerOffset - cornerSize
    );

    let yPos = 50;

    // "KARUNA INTERNATIONAL" - Large dark blue sans-serif
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 58, 138); // Dark blue
    doc.text("KARUNA INTERNATIONAL", centerX, yPos, {
      align: "center",
    });
    yPos += 20;

    // "CERTIFICATE OF EXCELLENCE" with decorative lines
    const certType =
      submission.score >= 80
        ? "EXCELLENCE"
        : submission.score >= 60
          ? "MERIT"
          : "PARTICIPATION";
    doc.setFontSize(32);
    doc.setFont("times", "bold");
    doc.setTextColor(30, 58, 138); // Dark blue
    const certText = `CERTIFICATE OF ${certType}`;
    const certTextWidth = doc.getTextWidth(certText);
    const lineLength = 30;

    // Decorative yellow lines on sides
    doc.setDrawColor(255, 193, 7); // Yellow
    doc.setLineWidth(1.5);
    doc.line(
      centerX - certTextWidth / 2 - lineLength - 10,
      yPos,
      centerX - certTextWidth / 2 - 10,
      yPos
    );
    doc.line(
      centerX + certTextWidth / 2 + 10,
      yPos,
      centerX + certTextWidth / 2 + lineLength + 10,
      yPos
    );

    doc.text(certText, centerX, yPos, {
      align: "center",
    });
    yPos += 30;

    // "This is to certify that"
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(75, 85, 99); // Dark gray
    doc.text("This is to certify that", centerX, yPos, {
      align: "center",
    });
    yPos += 25;

    // School name - Large bold dark blue serif
    doc.setFontSize(26);
    doc.setFont("times", "bold");
    doc.setTextColor(30, 58, 138); // Dark blue
    doc.text(submission.school_name, centerX, yPos, {
      align: "center",
      maxWidth: pageWidth - 80,
    });
    yPos += 15;

    // KC No
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(75, 85, 99); // Dark gray
    doc.text(`KC No: ${submission.kc_no}`, centerX, yPos, {
      align: "center",
    });
    yPos += 20;

    // "has successfully participated in"
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(75, 85, 99); // Dark gray
    doc.text("has successfully participated in", centerX, yPos, {
      align: "center",
    });
    yPos += 18;

    // Activity name - Medium italicized dark blue serif
    doc.setFontSize(16);
    doc.setFont("times", "italic");
    doc.setTextColor(30, 58, 138); // Dark blue
    doc.text(`"${submission.activity_title}"`, centerX, yPos, {
      align: "center",
      maxWidth: pageWidth - 80,
    });
    yPos += 25;

    // Score - Bold dark gray
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(75, 85, 99); // Dark gray
    doc.text(`Score: ${submission.score}/100`, centerX, yPos, {
      align: "center",
    });
    yPos += 50;

    // Bottom section with Date, Seal, and Signatory
    const bottomY = pageHeight - 50;

    // Left side - Date
    doc.setDrawColor(75, 85, 99);
    doc.setLineWidth(0.5);
    doc.line(40, bottomY, 80, bottomY);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(75, 85, 99);
    doc.text("Date", 60, bottomY + 8, { align: "center" });
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    doc.text(dateStr, 60, bottomY + 15, { align: "center", maxWidth: 80 });

    // Center - Karuna Seal (circular)
    const sealRadius = 20;
    const sealY = bottomY + 10;
    doc.setDrawColor(30, 58, 138);
    doc.setLineWidth(2);
    // Draw circle manually using arcs
    const steps = 64;
    const angleStep = (2 * Math.PI) / steps;
    let prevX = centerX + sealRadius;
    let prevY = sealY;
    for (let i = 1; i <= steps; i++) {
      const angle = i * angleStep;
      const x = centerX + sealRadius * Math.cos(angle);
      const y = sealY + sealRadius * Math.sin(angle);
      doc.line(prevX, prevY, x, y);
      prevX = x;
      prevY = y;
    }
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 58, 138);
    doc.text("KARUNA", centerX, bottomY + 5, { align: "center" });
    doc.text("SEAL", centerX, bottomY + 12, { align: "center" });

    // Right side - Authorized Signatory
    doc.setDrawColor(75, 85, 99);
    doc.setLineWidth(0.5);
    doc.line(pageWidth - 80, bottomY, pageWidth - 40, bottomY);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(75, 85, 99);
    doc.text("Authorized Signatory", pageWidth - 60, bottomY + 8, {
      align: "center",
    });

    return doc;
  };

  // Preview certificate (opens in modal)
  const previewCertificate = async (submission: CertificateSubmission) => {
    try {
      const doc = await generateCertificatePDFDoc(submission);

      // Convert PDF to canvas and then to image for clean preview
      // Get PDF as array buffer
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Create a temporary iframe to render PDF, then capture as image
      // Alternative: use object tag with specific styling
      // For now, use embed with toolbar hidden
      const embedUrl = `${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
      setPreviewImageUrl(embedUrl);
      setPreviewSubmission(submission);
      setPreviewOpen(true);
    } catch (error) {
      console.error("Error previewing certificate:", error);
      toast({
        title: "Error",
        description: "Failed to preview certificate",
        variant: "destructive",
      });
    }
  };

  // Download from preview modal
  const downloadFromPreview = async () => {
    if (previewSubmission) {
      await generateCertificatePDF(previewSubmission);
      setPreviewOpen(false);
    }
  };

  // Generate all certificates
  const generateAllCertificates = async () => {
    if (filteredSubmissions.length === 0) {
      toast({
        title: "No Certificates",
        description: "No eligible submissions to generate certificates for",
        variant: "destructive",
      });
      return;
    }

    try {
      toast({
        title: "Generating Certificates",
        description: `Generating ${filteredSubmissions.length} certificates...`,
      });

      // Generate certificates one by one with a small delay
      for (let i = 0; i < filteredSubmissions.length; i++) {
        await generateCertificatePDF(filteredSubmissions[i]);
        // Small delay between downloads to avoid browser blocking
        if (i < filteredSubmissions.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      toast({
        title: "Success",
        description: `All ${filteredSubmissions.length} certificates generated successfully`,
      });
    } catch (error) {
      console.error("Error generating certificates:", error);
      toast({
        title: "Error",
        description: "Failed to generate some certificates",
        variant: "destructive",
      });
    }
  };

  const loading = submissionsLoading;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Certificates</h1>
            <p className="mt-1 text-muted-foreground">
              Generate and download certificates for approved submissions
            </p>
          </div>
          <Button
            onClick={generateAllCertificates}
            disabled={loading || filteredSubmissions.length === 0}
            className="gap-2 bg-[#404bb5] hover:bg-[#404bb5]/80"
          >
            <Users className="h-4 w-4" />
            Generate All
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div className="mb-1 text-3xl font-bold text-blue-600">
                {stats.total}
              </div>
              <div className="text-sm text-muted-foreground">
                Total Eligible
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div className="mb-1 text-3xl font-bold text-orange-600">
                {stats.excellence}
              </div>
              <div className="text-sm text-muted-foreground">
                Excellence (80+)
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div className="mb-1 text-3xl font-bold text-gray-600">
                {stats.merit}
              </div>
              <div className="text-sm text-muted-foreground">Merit (60-79)</div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div className="mb-1 text-3xl font-bold text-red-600">
                {stats.participation}
              </div>
              <div className="text-sm text-muted-foreground">Participation</div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <Card className="border-border/50 bg-muted/30">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by school name, KC number, or activity..."
                className="pl-9"
              />
            </div>
          </CardContent>
        </Card>

        {/* Certificate List */}
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">
                No eligible submissions found matching your search.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <ListCardViewToggle
                viewMode={viewMode}
                onViewChange={setViewMode}
              />
            </div>

            <Card className="border-border/50 shadow-medium">
              <CardContent className="p-0">
                {viewMode === "list" ? (
                  <div className="divide-y divide-border">
                    {pagedCertSubmissions.map(submission => (
                      <div
                        key={submission.id}
                        className="flex flex-col gap-3 p-4 transition-colors hover:bg-muted/30 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex flex-wrap items-center gap-3">
                            <h3 className="font-semibold text-foreground">
                              {submission.school_name}
                            </h3>
                            {getCertificateBadge(submission.score)}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            KC No: {submission.kc_no} • Activity:{" "}
                            {submission.activity_title} • Score:{" "}
                            {submission.score}/100
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <Button
                            onClick={() => previewCertificate(submission)}
                            variant="outline"
                            size="sm"
                            className="gap-2"
                          >
                            <Eye className="h-4 w-4" />
                            Preview
                          </Button>
                          <Button
                            onClick={() => generateCertificatePDF(submission)}
                            size="sm"
                            className="gap-2 bg-[#404bb5] hover:bg-[#404bb5]/80"
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-4 p-4 md:grid-cols-2 lg:grid-cols-3">
                    {pagedCertSubmissions.map(submission => (
                      <Card
                        key={submission.id}
                        className="border border-border shadow-sm"
                      >
                        <CardContent className="space-y-3 p-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="line-clamp-2 font-semibold">
                              {submission.school_name}
                            </h3>
                            {getCertificateBadge(submission.score)}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {submission.kc_no}
                          </p>
                          <p className="line-clamp-2 text-sm text-muted-foreground">
                            {submission.activity_title}
                          </p>
                          <p className="text-sm font-medium">
                            Score: {submission.score}/100
                          </p>
                          <div className="flex flex-wrap gap-2 pt-2">
                            <Button
                              onClick={() => previewCertificate(submission)}
                              variant="outline"
                              size="sm"
                              className="gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              Preview
                            </Button>
                            <Button
                              onClick={() => generateCertificatePDF(submission)}
                              size="sm"
                              className="gap-2 bg-[#404bb5] hover:bg-[#404bb5]/80"
                            >
                              <Download className="h-4 w-4" />
                              Download
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <KarunaPagination
              page={certPage}
              totalPages={certTotalPages}
              totalItems={certFilteredTotal}
              pageSize={certPageSize}
              onPageChange={setCertPage}
              onPageSizeChange={setCertPageSize}
            />
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onOpenChange={open => {
          setPreviewOpen(open);
          if (!open) {
            // Clean up preview image URL when dialog closes
            if (previewImageUrl) {
              URL.revokeObjectURL(previewImageUrl);
            }
            setPreviewImageUrl(null);
            setPreviewSubmission(null);
          }
        }}
      >
        <DialogContent className="flex h-[90vh] w-full max-w-5xl flex-col p-0">
          <DialogHeader className="border-b p-6 pb-4">
            <DialogTitle>Certificate Preview</DialogTitle>
          </DialogHeader>
          <div className="flex flex-1 items-center justify-center overflow-auto bg-gray-50 p-6">
            {previewImageUrl && (
              <object
                data={previewImageUrl}
                type="application/pdf"
                className="w-full rounded shadow-lg"
                style={{ maxHeight: "calc(90vh - 200px)", minHeight: "600px" }}
              >
                <p className="p-8 text-center text-muted-foreground">
                  Unable to display PDF preview. Please download the
                  certificate.
                </p>
              </object>
            )}
          </div>
          <DialogFooter className="border-t p-6 pt-4">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
            <Button
              onClick={downloadFromPreview}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCertificates;
